import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, TOKENS } from '../context/ThemeContext';
import BACKEND_URL from '../config';
import SEO from '../components/SEO';

function StatCard({ label, value, sub, accent, c }) {
  return (
    <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '20px 24px', boxShadow: c.shadow }}>
      <p style={{ fontSize: '11px', fontWeight: 600, color: c.secondary, margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '1px' }}>{label}</p>
      <p style={{ fontSize: '32px', fontWeight: 800, color: accent || c.text, margin: '0 0 4px', letterSpacing: '-1px' }}>{value}</p>
      {sub && <p style={{ fontSize: '12px', color: c.secondary, margin: 0 }}>{sub}</p>}
    </div>
  );
}

function DiffBadge({ label, count, color, bg }) {
  return (
    <span style={{ fontSize: '11px', fontWeight: 600, color, background: bg, padding: '2px 8px', borderRadius: '10px', marginRight: '6px' }}>
      {label} {count}
    </span>
  );
}

export default function TeamDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const c = TOKENS[theme];

  const [loading, setLoading] = useState(true);
  const [orgInfo, setOrgInfo] = useState(null);
  const [, setAdmin] = useState(null); // eslint-disable-line no-unused-vars
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const difficultyColor = { Easy: c.success, Medium: c.warning, Hard: c.error };
  const difficultyBg = {
    Easy:   theme === 'light' ? '#d1fae5' : '#051a10',
    Medium: theme === 'light' ? '#fef3c7' : '#1a1000',
    Hard:   theme === 'light' ? '#fee2e2' : '#1a0808',
  };

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    loadData();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const orgRes = await fetch(`${BACKEND_URL}/org/mine/${user.id}`);
      const orgData = await orgRes.json();

      if (!orgData.inOrg) {
        navigate('/team');
        return;
      }
      setOrgInfo(orgData);

      const [membersRes, dashRes] = await Promise.all([
        fetch(`${BACKEND_URL}/org/${orgData.org.id}/members`),
        fetch(`${BACKEND_URL}/org/${orgData.org.id}/dashboard`),
      ]);
      const membersData = await membersRes.json();
      const dashData = await dashRes.json();

      setAdmin(membersData.admin || null);
      setMembers(membersData.members || []);
      setPendingInvites(membersData.pendingInvites || []);
      setDashboard(dashData);
    } catch (e) {
      console.error('Team dashboard load error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true); setInviteMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/org/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId: orgInfo.org.id, email: inviteEmail.trim(), invitedBy: user.id }),
      });
      const data = await res.json();
      if (!res.ok) {
        setInviteMsg({ type: 'error', text: data.error || 'Failed to invite' });
      } else {
        setInviteMsg({ type: 'success', text: `Invite sent to ${inviteEmail.trim()}` });
        setInviteEmail('');
        loadData();
      }
    } catch (e) {
      setInviteMsg({ type: 'error', text: e.message });
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (memberId) => {
    if (!window.confirm('Remove this member from the team?')) return;
    try {
      await fetch(`${BACKEND_URL}/org/${orgInfo.org.id}/members/${memberId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestedBy: user.id }),
      });
      loadData();
    } catch (e) {
      console.error('Remove error:', e);
    }
  };

  const inviteLink = orgInfo ? `${window.location.origin}/join/${orgInfo.org.slug}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  if (!user) return (
    <div style={{ background: c.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: c.secondary }}>Sign in to view your team</p>
    </div>
  );

  if (loading) return (
    <div style={{ background: c.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: c.secondary }}>Loading team...</p>
    </div>
  );

  if (!orgInfo) return null;

  const isAdmin = orgInfo.role === 'admin';

  return (
    <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', -apple-system, sans-serif" }}>

      <SEO
        title={orgInfo.org.name ? `${orgInfo.org.name} — Team Dashboard` : 'Team Dashboard'}
        description="View your Salesforce team's coding progress, invite members, and track challenge completions."
        url="https://trailcode.org/team/dashboard"
        noIndex={true}
      />

      {/* Navbar */}
      <nav style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0 clamp(24px, 6vw, 100px)', height: '58px',
        background: c.navBg, backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${c.border}`,
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <span style={{ fontSize: '20px', fontWeight: 800, cursor: 'pointer', letterSpacing: '-0.5px', color: c.text }}
          onClick={() => navigate('/')}>
          Trail<span style={{ color: c.accent }}>Code</span>
        </span>
        <span style={{ color: c.accent, fontSize: '14px', fontWeight: 600 }}>👥 {orgInfo.org.name}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src={user.user_metadata?.avatar_url} alt="avatar" style={{ width: '28px', height: '28px', borderRadius: '50%', border: `1.5px solid ${c.border}` }} />
        </div>
      </nav>

      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '40px clamp(24px, 4vw, 60px) 80px' }}>

        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '26px', fontWeight: 800, margin: '0 0 4px', letterSpacing: '-0.5px' }}>{orgInfo.org.name}</h1>
          <p style={{ fontSize: '13px', color: c.secondary, margin: 0 }}>
            {isAdmin ? '👑 You manage this team' : '👤 Team Member'} · {members.length} / {orgInfo.org.seat_limit} seats used
          </p>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '32px' }}>
          <StatCard label="Team Members" value={dashboard?.memberCount || 0} c={c} />
          <StatCard label="Total Challenges Solved" value={dashboard?.totalSolved || 0} accent={c.accent} c={c} />
          <StatCard label="Seats Available" value={`${orgInfo.org.seat_limit - members.length}`} sub={`of ${orgInfo.org.seat_limit} total`} c={c} />
        </div>

        {/* Admin: Invite Section */}
        {isAdmin && (
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '20px', marginBottom: '24px', boxShadow: c.shadow }}>
            <p style={{ fontSize: '11px', fontWeight: 600, color: c.secondary, margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Invite Members</p>

            {/* Invite link */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <input
                readOnly
                value={inviteLink}
                style={{ flex: 1, minWidth: '200px', padding: '10px', borderRadius: '6px', border: `1px solid ${c.border}`, background: c.bg, color: c.secondary, fontSize: '12px', fontFamily: 'monospace' }}
              />
              <button onClick={copyLink} style={{ background: c.accentBg, border: `1px solid ${c.accent}`, color: c.accent, padding: '10px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                {copySuccess ? '✅ Copied!' : '📋 Copy Link'}
              </button>
            </div>

            {/* Invite by email */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <input
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleInvite()}
                placeholder="developer@company.com"
                style={{ flex: 1, minWidth: '200px', padding: '10px', borderRadius: '6px', border: `1px solid ${c.border}`, background: c.bg, color: c.text, fontSize: '13px' }}
              />
              <button onClick={handleInvite} disabled={inviting} style={{ background: c.accent, color: theme === 'dark' ? c.bg : '#fff', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>
                {inviting ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
            {inviteMsg && (
              <p style={{ fontSize: '12px', color: inviteMsg.type === 'error' ? c.error : c.success, marginTop: '10px', marginBottom: 0 }}>
                {inviteMsg.text}
              </p>
            )}

            {/* Pending invites */}
            {pendingInvites.length > 0 && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${c.border}` }}>
                <p style={{ fontSize: '11px', color: c.secondary, marginBottom: '8px' }}>Pending invites:</p>
                {pendingInvites.map(inv => (
                  <span key={inv.id} style={{ display: 'inline-block', background: c.bg, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: c.secondary, marginRight: '8px', marginBottom: '8px' }}>
                    ⏳ {inv.email}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Members List */}
        <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '20px', boxShadow: c.shadow }}>
          <p style={{ fontSize: '11px', fontWeight: 600, color: c.secondary, margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Team Members {members.length === 0 && '— none yet'}
          </p>

          {members.length === 0 && (
            <p style={{ fontSize: '13px', color: c.muted, margin: 0 }}>
              Invite developers using the link or email form above. Their progress will show up here once they join and start solving challenges.
            </p>
          )}

          {members.map((m, i) => (
            <div key={m.user_id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 0',
              borderBottom: i < members.length - 1 ? `1px solid ${c.border}` : 'none',
              flexWrap: 'wrap', gap: '10px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <img src={m.users?.avatar_url} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', border: `1.5px solid ${c.border}` }} />
                <div>
                  <p style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 2px' }}>{m.users?.name || m.users?.email || 'Unknown'}</p>
                  <p style={{ fontSize: '11px', color: c.secondary, margin: 0 }}>{m.users?.email}</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <div>
                  <DiffBadge label="Easy" count={m.byDifficulty?.Easy || 0} color={difficultyColor.Easy} bg={difficultyBg.Easy} />
                  <DiffBadge label="Medium" count={m.byDifficulty?.Medium || 0} color={difficultyColor.Medium} bg={difficultyBg.Medium} />
                  <DiffBadge label="Hard" count={m.byDifficulty?.Hard || 0} color={difficultyColor.Hard} bg={difficultyBg.Hard} />
                </div>
                <div style={{ textAlign: 'right', minWidth: '90px' }}>
                  <p style={{ fontSize: '13px', fontWeight: 700, color: c.accent, margin: 0 }}>{m.challenges_solved} solved · {m.total_points} pts</p>
                  <p style={{ fontSize: '11px', color: c.secondary, margin: 0 }}>🔥 {m.current_streak} day streak</p>
                </div>
                {isAdmin && (
                  <button onClick={() => handleRemove(m.user_id)} style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.error, padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <button onClick={() => navigate('/')} style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
            ← Back to TrailCode
          </button>
        </div>
      </div>
    </div>
  );
}